import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OrderCard from "@/components/OrderCard";
import AutoRefreshIndicator from "@/components/AutoRefreshIndicator";
import PullToRefreshContainer from "@/components/PullToRefreshContainer";
import CustomerActiveOrdersBlock from "@/components/CustomerActiveOrdersBlock";
import { formatCHF } from "@/lib/format";

export default function CustomerHome() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
      setLastUpdated(new Date());
    } catch { /* silent on auto-refresh */ }
    finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const active = orders.filter((o) => !["delivered", "cancelled"].includes(o.status));
  const totalSpent = orders.filter((o) => o.status === "delivered").reduce((s, o) => s + (o.final_price ?? o.price ?? 0), 0);

  return (
    <PullToRefreshContainer onRefresh={() => load(true)}>
    <div className="space-y-6" data-testid="customer-home">
      {/* Phase 43.59 — Active orders live block (in alto, sopra hero) */}
      <CustomerActiveOrdersBlock orders={orders} />

      {/* Hero CTA card */}
      <section className="relative overflow-hidden rounded-3xl bg-dapnow-ink text-white p-6 sm:p-8">
        <div className="absolute inset-0 map-bg opacity-30" />
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-dapnow-blue/40 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-dapnow-red/30 blur-3xl" />
        <div className="relative">
          <p className="overline text-white/70">Ciao {user?.name?.split(" ")[0]}</p>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter max-w-md leading-tight">
            Cosa spedisci oggi?
          </h1>
          <p className="mt-2 text-white/70 text-sm max-w-md">
            Standard: consegna in giornata con flotta interna in Ticino e nei Grigioni italiano, entro le 21:00 per ritiri entro le 18:00. Il team opera tutti i giorni dalle 06:30 alle 21:00; per ritiri tra le 18:00 e le 21:00, contatta DapNow per concordare i tempi prima di prenotare.
          </p>
          <p className="mt-2 text-white/70 text-xs max-w-md">Shared per driver privati è in arrivo. <Link to="/become-driver" className="font-semibold text-white underline">Richiedi informazioni</Link>.</p>
          <Link
            to="/app/new"
            data-testid="customer-new-order-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white text-dapnow-ink px-5 h-12 font-semibold tracking-tight shadow-sm hover:bg-white/90 transition"
          >
            <Plus className="h-4 w-4" /> Nuova consegna
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="card-soft p-5">
          <p className="overline">Ordini totali</p>
          <p className="font-display text-3xl font-bold tracking-tight mt-1">{orders.length}</p>
        </div>
        <div className="card-soft p-5">
          <p className="overline">In corso</p>
          <p className="font-display text-3xl font-bold tracking-tight mt-1">{active.length}</p>
        </div>
        <div className="card-soft p-5 col-span-2 sm:col-span-1">
          <p className="overline">Addebiti sulle consegne</p>
          <p className="font-display text-3xl font-bold tracking-tight mt-1">{formatCHF(totalSpent)}</p>
          <p className="text-xs text-dapnow-muted mt-2">Consegne completate, al netto dei crediti applicati. Acquisti di pacchetti e abbonamenti esclusi.</p>
        </div>
      </section>

      {/* Active orders */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-display text-xl font-bold tracking-tight">Consegne in corso</h2>
          <div className="flex items-center gap-3">
            <AutoRefreshIndicator
              lastUpdated={lastUpdated}
              intervalSec={30}
              loading={refreshing}
              onRefresh={() => load(true)}
            />
            <Link to="/app/orders" className="text-sm font-semibold text-dapnow-blue inline-flex items-center gap-1">
              Tutte <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        {loading ? (
          <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>
        ) : active.length === 0 ? (
          <div className="card-soft p-8 text-center">
            <Sparkles className="h-6 w-6 text-dapnow-blue mx-auto" />
            <p className="mt-3 font-display text-lg font-semibold tracking-tight">Nessuna consegna in corso</p>
            <p className="text-sm text-dapnow-muted mt-1">Organizza la tua prossima consegna.</p>
            <Link to="/app/new" className="btn-primary mt-5">Crea ordine</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {active.map((o) => (
              <OrderCard key={o.id} order={o} to={`/app/orders/${o.id}`} testId={`customer-active-${o.id}`} />
            ))}
          </div>
        )}
      </section>
    </div>
    </PullToRefreshContainer>
  );
}
